import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toast } from "sonner";
import { type AxiosError } from "axios";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Field, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Separator } from "@libs/zapi-ui/ds/components/separator";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { CheckIcon } from "@libs/zapi-ui/assets";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useChangePassword } from "../services/useChangePassword";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

const NewPasswordSchema = z.string().min(8).max(32).regex(/[0-9]/).regex(/[A-Z]/);

export function ChangePasswordSection() {
  const { t } = useTranslation(["settings", "common"]);
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const idPrefix = useId();

  const { mutateAsync: changePassword, isPending } = useChangePassword();
  const refetch = useMeStore((s) => s.refetch);

  const isLengthValid = form.newPassword.length >= 8;
  const hasNumber = /[0-9]/.test(form.newPassword);
  const hasCapital = /[A-Z]/.test(form.newPassword);
  const passwordsMatch = form.newPassword.length > 0 && form.newPassword === form.confirmPassword;
  const isValid =
    form.currentPassword.length > 0 && NewPasswordSchema.safeParse(form.newPassword).success && passwordsMatch;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    try {
      await changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      toast.success(t("password.changed"));
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      await refetch?.();
    } catch (err) {
      const message = (err as AxiosError<{ message: string }>)?.response?.data?.message ?? t("password.changeFailed");
      toast.error(message);
    }
  }

  function passwordInput(label: string, key: keyof typeof form, show: boolean, setShow: (v: boolean) => void) {
    const testIdSlug = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    const inputId = `${idPrefix}-${key}`;
    return (
      <div className="relative w-[320px]">
        <Field className="gap-2 w-full">
          <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
          <Input
            id={inputId}
            data-testid={`change-password-${testIdSlug}-input`}
            type={show ? "text" : "password"}
            value={form[key]}
            onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value.trim() }))}
            disabled={isPending}
            className="pe-12"
          />
        </Field>
        <button
          type="button"
          data-testid={`change-password-toggle-${testIdSlug}-visibility-button`}
          onClick={() => setShow(!show)}
          className="absolute end-0 bottom-0 h-[32px] flex items-center pe-3 text-[14px] font-medium"
        >
          {show ? t("common.hide") : t("common.show")}
        </button>
      </div>
    );
  }

  function criterion(label: string, met: boolean) {
    return (
      <div className="flex gap-2 items-center">
        <span
          className={cn(
            "w-[14px] h-[14px] rounded-full flex items-center justify-center",
            met ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
          )}
        >
          {met && <CheckIcon width={8} height={8} />}
        </span>
        <p className={cn("typo-caption", met ? "text-successGreen" : "text-innoveraGrayscale-500")}>{label}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[18px] bg-white">
      <p className="typo-h6 text-innoveraZinc-900">{t("password.heading")}</p>

      <Separator />

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {passwordInput(t("password.current"), "currentPassword", showCurrent, setShowCurrent)}

        <div className="flex flex-col gap-4">
          {passwordInput(t("password.new"), "newPassword", showNew, setShowNew)}
          {passwordInput(t("password.confirm"), "confirmPassword", showConfirm, setShowConfirm)}

          <div className="flex flex-col gap-2">
            {criterion(t("password.minLength"), isLengthValid)}
            {criterion(t("password.hasNumber"), hasNumber)}
            {criterion(t("password.hasCapital"), hasCapital)}
            {criterion(t("password.matches"), passwordsMatch)}
          </div>
        </div>

        <Button
          data-testid="change-password-submit-button"
          className="w-fit"
          type="submit"
          disabled={!isValid || isPending}
        >
          {isPending ? (
            <>
              <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
              {t("common.saving")}
            </>
          ) : (
            t("common.save")
          )}
        </Button>
      </form>
    </div>
  );
}
